import { useEffect, useState } from "react";
import { ApiError, type ReportRow, type SalesReport, api, formatMoney } from "./api";
import { shortDay, singaporeToday } from "./dates";
import { PageHeader } from "./layout/Layout";
import { Alert, Box, Empty, Loading, StatCard, Table, Th, TwoLine } from "./ui";

/**
 * Reports (quotation A22) and the accountant's file (A23).
 *
 * The questions asked at the end of a month, on one page: what was sold, what it cost,
 * what was made on it, who sold it and which show was worth going to. The figures come
 * from the same rows the other screens use, so this can never quietly disagree with them.
 */
export function Reports(): React.ReactElement {
  const [period, setPeriod] = useState(thisMonth);
  const [report, setReport] = useState<SalesReport | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    let live = true;
    setLoading(true);
    api
      .salesReport(period.from, period.to)
      .then((result) => {
        if (live) setReport(result.report);
      })
      .catch((problem: unknown) => {
        if (live) setError(problem instanceof ApiError ? problem.message : "Could not generate the report");
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [period.from, period.to]);

  const margin =
    report && report.sold.soldCents > 0 ? Math.round((report.sold.profitCents / report.sold.soldCents) * 100) : 0;

  return (
    <>
      <PageHeader title="Reports" subtitle="Sales, product costs, gross profit and accounting exports." />

      <Box title="Date range">
        <div className="flex flex-wrap items-center gap-2">
          <input
            id="report-from"
            type="date"
            aria-label="From"
            className="form-control field-narrow"
            value={period.from}
            max={period.to}
            onChange={(changed) => setPeriod({ ...period, from: changed.target.value })}
          />
          <span className="text-[#8c9097] dark:text-white/50">to</span>
          <input
            id="report-to"
            type="date"
            aria-label="To"
            className="form-control field-narrow"
            value={period.to}
            max={singaporeToday()}
            onChange={(changed) => setPeriod({ ...period, to: changed.target.value })}
          />
          <button type="button" className="ti-btn ti-btn-light !mb-0" onClick={() => setPeriod(thisMonth())}>
            This month
          </button>
          <button type="button" className="ti-btn ti-btn-light !mb-0" onClick={() => setPeriod(lastMonth())}>
            Last month
          </button>
          <div className="flex-1" />
          <a
            className="ti-btn ti-btn-primary-full !mb-0"
            href={`/api/reports/export?from=${period.from}&to=${period.to}`}
            download
          >
            <i className="ri-download-2-line" /> Export accounting records
          </a>
        </div>
        <p className="text-[0.75rem] text-[#8c9097] dark:text-white/50 mt-3 mb-0">
          The Excel file contains every sale, buyback, purchase, payment voucher and bank transaction for the selected dates, one row each.
        </p>
      </Box>

      {error ? <Alert tone="danger">{error}</Alert> : null}
      {loading || !report ? (
        <Loading />
      ) : (
        <>
          <div className="grid grid-cols-12 gap-x-6">
            <div className="xl:col-span-3 md:col-span-6 col-span-12">
              <StatCard
                label="Paid sales total"
                value={formatMoney(report.sold.soldCents)}
                icon="ti ti-cash"
                tone="primary"
                note={`${report.sold.salesCount} paid ${report.sold.salesCount === 1 ? "sale" : "sales"}`}
            />
            </div>
            <div className="xl:col-span-3 md:col-span-6 col-span-12">
              <StatCard
                label="Cost of goods sold"
                value={formatMoney(report.sold.costCents)}
                icon="ti ti-package"
                tone="secondary"
                note="Recorded cost of the inventory sold"
            />
            </div>
            <div className="xl:col-span-3 md:col-span-6 col-span-12">
              <StatCard
                label="Gross profit on paid sales"
                value={formatMoney(report.sold.profitCents)}
                icon="ti ti-trending-up"
                tone={report.sold.profitCents >= 0 ? "success" : "danger"}
                note={`${margin}% of customer payments`}
            />
            </div>
            <div className="xl:col-span-3 md:col-span-6 col-span-12">
              <StatCard
                label="Inventory purchases"
                value={formatMoney(report.bought.purchasesCents + report.bought.buybacksCents)}
                icon="ti ti-shopping-cart"
                tone="info"
                note={`Includes ${formatMoney(report.bought.buybacksCents)} in customer buybacks`}
            />
            </div>
          </div>

          <Box title="Payments by method">
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 text-center">
            <Figure label="Cash" value={report.sold.cashCents} />
            <Figure label="PayNow" value={report.sold.paynowCents} />
            <Figure label="TikTok" value={report.sold.tiktokCents} />
            <Figure label="Outstanding customer payments" value={report.sold.unpaidCents} />
            <Figure label="Total sales including unpaid" value={report.sold.soldCents + report.sold.unpaidCents} />
            </div>
            <p className="text-[0.75rem] text-[#8c9097] dark:text-white/50 mt-4 mb-0">
              Gross profit on paid sales is customer payments less the recorded cost of the products sold. It excludes
              rent, wages and other operating expenses; the accounting records export provides what the accountant
              needs for those.
            </p>
          </Box>

          {report.tiktok ? (
            <Box title="TikTok Shop">
              <div className="grid grid-cols-3 gap-4 text-center">
                <Figure label="TikTok sales" value={report.tiktok.soldCents} />
                <Figure label="TikTok payouts received" value={report.tiktok.paidOutCents} />
                <Figure label="Difference" value={report.tiktok.differenceCents} />
              </div>
              <p className="text-[0.75rem] text-[#8c9097] dark:text-white/50 mt-4 mb-0">
                The difference is what TikTok retained as fees, refunded, or has not yet paid out. TikTok's order
                file does not specify which, so the system does not estimate it. A payout counts here once it is
                matched to TikTok payout on the Bank reconciliation screen.
              </p>
            </Box>
          ) : null}

          <div className="grid grid-cols-12 gap-x-6">
            <div className="xl:col-span-6 col-span-12">
            <Listing title="Sales by product" rows={report.byProduct} what="thing" />
            </div>
            <div className="xl:col-span-6 col-span-12">
            <Listing title="Sales by location or event" rows={report.byEvent} what="place" />
            </div>
            <div className="xl:col-span-6 col-span-12">
            <Listing title="Sales by salesperson" rows={report.byPerson} what="person" />
            </div>
            <div className="xl:col-span-6 col-span-12">
            <Listing
              title="Daily sales"
              rows={report.byDay.map((row) => ({ ...row, label: shortDay(row.label) }))}
              what="day"
            />
            </div>
          </div>

          <Box title="Funds outgoing by category" count={report.spending.length}>
            {report.spending.length === 0 ? (
              <Empty icon="ri-price-tag-3-line">
                No transactions were matched to a category in these dates. Matching is done on the Bank reconciliation page.
              </Empty>
            ) : (
              <Table
                head={
                  <>
                    <Th>Transaction category</Th>
                    <Th end>Amount</Th>
                    <Th end>Transactions</Th>
                </>
                }
              >
                {report.spending.map((row) => (
                  <tr key={row.label} className="border-b border-defaultborder">
                    <td>{row.label}</td>
                    <td className={`!text-end font-semibold ${row.amountCents < 0 ? "text-danger" : ""}`}>
                      {formatMoney(row.amountCents)}
                    </td>
                    <td className="!text-end text-[#8c9097] dark:text-white/50">{row.lines}</td>
                  </tr>
                ))}
              </Table>
            )}
          </Box>
        </>
      )}
    </>
  );
}

function Figure({ label, value }: { readonly label: string; readonly value: number }): React.ReactElement {
  return (
    <div>
      <p className="text-[#8c9097] dark:text-white/50 text-[0.75rem] mb-1">{label}</p>
      <p className="font-semibold text-[1.25rem] mb-0">{formatMoney(value)}</p>
    </div>
  );
}

/** One breakdown: what it sold for, what it cost, and what that left. */
function Listing({
  title,
  rows,
  what,
}: {
  readonly title: string;
  readonly rows: readonly ReportRow[];
  readonly what: string;
}): React.ReactElement {
  const most = Math.max(1, ...rows.map((row) => row.soldCents));

  return (
    <Box title={title} count={rows.length}>
      {rows.length === 0 ? (
        <Empty icon="ri-bar-chart-line">No sales in the selected dates.</Empty>
      ) : (
        <Table
          head={
            <>
              <Th>
                {what === "day" ? "Date" : what === "person" ? "Salesperson" : what === "place" ? "Location or event" : "Product"}
              </Th>
              <Th end>{what === "thing" ? "Units sold" : "Number of sales"}</Th>
              <Th end>Paid sales</Th>
              <Th end>Gross profit</Th>
            </>
          }
        >
          {rows.map((row) => (
            <tr key={`${row.label}-${row.sub ?? ""}`} className="border-b border-defaultborder">
              <td>
              <TwoLine main={row.label} sub={row.sub ?? undefined} />
                {/* A bar behind the biggest seller, so the shape of the list reads at a glance. */}
              <span className="block mt-1 h-[3px] rounded-full bg-primary/70" style={{ width: `${Math.round((row.soldCents / most) * 100)}%` }} />
              </td>
              <td className="!text-end">{row.quantity}</td>
              <td className="!text-end font-semibold">{formatMoney(row.soldCents)}</td>
              <td className="!text-end text-[#8c9097] dark:text-white/50">
                {formatMoney(row.soldCents - row.costCents)}
              </td>
            </tr>
          ))}
        </Table>
      )}
    </Box>
  );
}

function thisMonth(): { from: string; to: string } {
  const today = singaporeToday();
  return { from: `${today.slice(0, 7)}-01`, to: today };
}

function lastMonth(): { from: string; to: string } {
  const today = singaporeToday();
  const [year, month] = today.split("-").map(Number) as [number, number, number];
  const before = month === 1 ? { year: year - 1, month: 12 } : { year, month: month - 1 };
  const first = `${before.year}-${String(before.month).padStart(2, "0")}-01`;
  const last = new Date(Date.UTC(before.year, before.month, 0)).toISOString().slice(0, 10);
  return { from: first, to: last };
}
